// src/constants/export.constants.ts
// Constantes y configuración para la página de Exportación PTEL

import { TipologiaPTEL } from '@/types/tipologia';
import type {
  TipologiaConfig,
  ConfianzaConfig,
  FormatConfig,
  SheetConfig,
  ExcelColumn,
} from '@/types/export.types';

/**
 * Configuración de tipologías PTEL (19 categorías macro).
 *
 * Orden coincide con el enum canónico `TipologiaPTEL` (`src/types/tipologia.ts`).
 * Los iconos/colores definitivos para las 11 categorías ampliadas en TUN2 se
 * afinarán en TUN4 con el catálogo completo de símbolos PTEL (`SimboloIcono`).
 */
export const TIPOLOGIAS: Record<TipologiaPTEL, TipologiaConfig> = {
  SANITARIO: {
    code: 'SAN',
    label: 'Sanitario',
    icon: '🏥',
    color: { dark: '#F87171', light: '#DC2626' },
  },
  EDUCATIVO: {
    code: 'EDU',
    label: 'Educativo',
    icon: '🎓',
    color: { dark: '#38BDF8', light: '#0369A1' },
  },
  POLICIAL: {
    code: 'POL',
    label: 'Policial',
    icon: '🚔',
    color: { dark: '#818CF8', light: '#4F46E5' },
  },
  BOMBEROS: {
    code: 'BOM',
    label: 'Bomberos',
    icon: '🧯',
    color: { dark: '#FB923C', light: '#EA580C' },
  },
  CULTURAL: {
    code: 'CUL',
    label: 'Cultural',
    icon: '🏛️',
    color: { dark: '#C084FC', light: '#9333EA' },
  },
  RELIGIOSO: {
    code: 'REL',
    label: 'Religioso',
    icon: '⛪',
    color: { dark: '#A1A1AA', light: '#71717A' },
  },
  DEPORTIVO: {
    code: 'DEP',
    label: 'Deportivo',
    icon: '🏟️',
    color: { dark: '#A3E635', light: '#65A30D' },
  },
  MUNICIPAL: {
    code: 'MUN',
    label: 'Municipal',
    icon: '🏢',
    color: { dark: '#94A3B8', light: '#64748B' },
  },
  SOCIAL: {
    code: 'SOC',
    label: 'Social',
    icon: '👥',
    color: { dark: '#F472B6', light: '#DB2777' },
  },
  COMBUSTIBLE: {
    code: 'COM',
    label: 'Combustible',
    icon: '⛽',
    color: { dark: '#FBBF24', light: '#D97706' },
  },
  EMERGENCIAS: {
    code: 'EME',
    label: 'Emergencias',
    icon: '🚒',
    color: { dark: '#F97316', light: '#C2410C' },
  },
  HIDRAULICO: {
    code: 'HID',
    label: 'Hidráulico',
    icon: '💧',
    color: { dark: '#22D3EE', light: '#0891B2' },
  },
  ENERGIA: {
    code: 'ENE',
    label: 'Energía',
    icon: '⚡',
    color: { dark: '#FACC15', light: '#CA8A04' },
  },
  TELECOM: {
    code: 'TEL',
    label: 'Telecomunicaciones',
    icon: '📡',
    color: { dark: '#A78BFA', light: '#7C3AED' },
  },
  VIAL: {
    code: 'VIA',
    label: 'Vial',
    icon: '🛣️',
    color: { dark: '#9CA3AF', light: '#6B7280' },
  },
  INDUSTRIAL: {
    code: 'IND',
    label: 'Industrial',
    icon: '🏭',
    color: { dark: '#78716C', light: '#57534E' },
  },
  RSU: {
    code: 'RSU',
    label: 'Residuos',
    icon: '♻️',
    color: { dark: '#86EFAC', light: '#16A34A' },
  },
  PUERTO: {
    code: 'PTO',
    label: 'Puerto',
    icon: '⚓',
    color: { dark: '#7DD3FC', light: '#0284C7' },
  },
  GENERICO: {
    code: 'GEN',
    label: 'Genérico',
    icon: '📍',
    color: { dark: '#6B7280', light: '#4B5563' },
  },
};

/**
 * Configuración de niveles de confianza
 */
export const CONFIANZA_CONFIG: ConfianzaConfig[] = [
  { id: 'alta', label: 'Alta', range: '≥90', colorKey: 'success', minScore: 90, maxScore: 100 },
  { id: 'media', label: 'Media', range: '70-89', colorKey: 'info', minScore: 70, maxScore: 89 },
  { id: 'baja', label: 'Baja', range: '50-69', colorKey: 'warning', minScore: 50, maxScore: 69 },
  { id: 'critica', label: 'Crítica', range: '<50', colorKey: 'error', minScore: 0, maxScore: 49 },
];

/**
 * Configuración de formatos de exportación
 */
export const EXPORT_FORMATS: FormatConfig[] = [
  {
    value: 'xlsx',
    label: 'Excel',
    extension: '.xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    description: 'Microsoft Excel 2016+',
    features: [
      '11 pestañas por tipología',
      '23 columnas de datos',
      'Formato condicional por score',
      'Filtros automáticos',
    ],
    requiresGeo: false,
  },
  {
    value: 'ods',
    label: 'LibreOffice',
    extension: '.ods',
    mimeType: 'application/vnd.oasis.opendocument.spreadsheet',
    description: 'LibreOffice Calc',
    features: [
      '11 pestañas por tipología',
      'Estándar ISO/IEC 26300',
      'Software libre compatible',
    ],
    requiresGeo: false,
  },
  {
    value: 'geojson',
    label: 'GeoJSON',
    extension: '.geojson',
    mimeType: 'application/geo+json',
    description: 'GeoJSON RFC 7946',
    features: [
      'CRS: WGS84 (EPSG:4326)',
      'Importación directa QGIS',
      'Compatible Leaflet/MapLibre',
      'Solo registros geolocalizados',
    ],
    requiresGeo: true,
  },
  {
    value: 'csv',
    label: 'CSV',
    extension: '.csv',
    mimeType: 'text/csv;charset=utf-8',
    description: 'CSV UTF-8 con BOM',
    features: [
      'Codificación UTF-8 con BOM',
      'Separador: punto y coma (;)',
      'Compatible Excel/Google Sheets',
      'Máxima compatibilidad',
    ],
    requiresGeo: false,
  },
  {
    value: 'kml',
    label: 'KML',
    extension: '.kml',
    mimeType: 'application/vnd.google-earth.kml+xml',
    description: 'Google Earth KML',
    features: [
      'Placemarks con iconos',
      'Carpetas por tipología',
      'Estilos personalizados',
      'Google Earth Pro compatible',
    ],
    requiresGeo: true,
  },
];

/**
 * Configuración de hojas Excel por tipología (19 categorías macro)
 */
export const SHEET_CONFIG: Record<string, SheetConfig> = {
  GENERAL: { name: 'General', color: 'FFE3F2FD' },
  SANITARIO: { name: 'Sanitario', color: 'FFFFEBEE' },
  EDUCATIVO: { name: 'Educativo', color: 'FFE3F2FD' },
  POLICIAL: { name: 'Seguridad', color: 'FFE8EAF6' },
  BOMBEROS: { name: 'Bomberos', color: 'FFFFE0B2' },
  CULTURAL: { name: 'Cultural', color: 'FFF3E5F5' },
  RELIGIOSO: { name: 'Religioso', color: 'FFFFF8E1' },
  DEPORTIVO: { name: 'Deportivo', color: 'FFE8F5E9' },
  MUNICIPAL: { name: 'Municipal', color: 'FFECEFF1' },
  SOCIAL: { name: 'Social', color: 'FFFCE4EC' },
  COMBUSTIBLE: { name: 'Combustible', color: 'FFFFF3CD' },
  EMERGENCIAS: { name: 'Emergencias', color: 'FFFFF3E0' },
  HIDRAULICO: { name: 'Hidraulico', color: 'FFE0F7FA' },
  ENERGIA: { name: 'Energia', color: 'FFFFFDE7' },
  TELECOM: { name: 'Telecomunicaciones', color: 'FFEDE7F6' },
  VIAL: { name: 'Vial', color: 'FFF5F5F5' },
  INDUSTRIAL: { name: 'Industrial', color: 'FFEFEBE9' },
  RSU: { name: 'Residuos', color: 'FFE8F5E9' },
  PUERTO: { name: 'Puerto', color: 'FFE1F5FE' },
  GENERICO: { name: 'Otros', color: 'FFF5F5F5' },
  SIN_GEOLOCALIZAR: { name: 'Sin_Geolocalizar', color: 'FFFFCDD2' },
  RESUMEN: { name: 'Resumen', color: 'FFFFFFFF' },
};

/**
 * Columnas de exportación Excel (orden definitivo)
 */
export const EXCEL_COLUMNS: ExcelColumn[] = [
  { header: 'ID', key: 'id', width: 8 },
  { header: 'NOMBRE', key: 'nombre', width: 35 },
  { header: 'TIPOLOGIA', key: 'tipologia', width: 15 },
  { header: 'TIPO_ORIGINAL', key: 'tipoOriginal', width: 20 },
  { header: 'DIRECCION', key: 'direccion', width: 40 },
  // DIRCAJA-b: siempre a la derecha de DIRECCION. Si el fichero exportado se vuelve a
  // cargar, el extractor toma la primera cabecera que casa con «dirección».
  { header: 'DIRECCION_ORIGINAL', key: 'direccionOriginal', width: 40 },
  { header: 'MUNICIPIO', key: 'municipio', width: 20 },
  { header: 'PROVINCIA', key: 'provincia', width: 15 },
  { header: 'CODIGO_POSTAL', key: 'codigoPostal', width: 12 },
  { header: 'CP_FUENTE', key: 'codigoPostalSource', width: 12 },
  { header: 'TELEFONO', key: 'telefono', width: 15 },
  { header: 'EMAIL', key: 'email', width: 30 },
  { header: 'OBSERVACIONES', key: 'observaciones', width: 40 },
  { header: 'X_INICIAL', key: 'xInicial', width: 15, style: { numFmt: '0.00' } },
  { header: 'Y_INICIAL', key: 'yInicial', width: 15, style: { numFmt: '0.00' } },
  { header: 'SISTEMA_INICIAL', key: 'sistemaInicial', width: 15 },
  { header: 'X_UTM30', key: 'xUtm30', width: 15, style: { numFmt: '0.00' } },
  { header: 'Y_UTM30', key: 'yUtm30', width: 15, style: { numFmt: '0.00' } },
  { header: 'LON_WGS84', key: 'lonWgs84', width: 12, style: { numFmt: '0.000000' } },
  { header: 'LAT_WGS84', key: 'latWgs84', width: 12, style: { numFmt: '0.000000' } },
  { header: 'SCORE', key: 'score', width: 10, style: { alignment: { horizontal: 'center' } } },
  { header: 'CONFIANZA', key: 'confianza', width: 12 },
  { header: 'FUENTES', key: 'fuentes', width: 40 },
  { header: 'NUM_FUENTES', key: 'numFuentes', width: 12 },
  { header: 'GEOCODIFICADO', key: 'geocodificado', width: 15 },
  { header: 'PROCEDENCIA', key: 'procedencia', width: 12 }, // P4PROC: vía de la coordenada
  { header: 'TIPO_GEO', key: 'tipoGeo', width: 20 },
  { header: 'AFORO_DECLARADO', key: 'aforoDeclarado', width: 16, style: { alignment: { horizontal: 'center' } } },
  { header: 'AFORO_UNIDAD', key: 'aforoUnidad', width: 14 },
  { header: 'AFORO_FUENTE', key: 'aforoFuente', width: 16 },
  { header: 'AFORO_CONFIANZA', key: 'aforoConfianza', width: 16 },
  // ALBTIPO: ¿sirve como albergue? (estado + motivo)
  { header: 'ALB_ESTADO', key: 'albergueEstado', width: 14 },
  { header: 'ALB_MOTIVO', key: 'albergueMotivo', width: 50 },
  // ALBCAP-b: capacidad por la regla del plan (DGPC 1989). Vacío = sin ficha o no calculable; 0 = 0 plazas.
  { header: 'ALB_CAPACIDAD', key: 'albCapacidad', width: 14, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_CAP_SUPERFICIE', key: 'albCapSuperficie', width: 18, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_CAP_LIMITE', key: 'albCapLimite', width: 14 },
  { header: 'ALB_CAP_AVISOS', key: 'albCapAvisos', width: 50 },
  // ALBFICHA-b: ficha de albergue del plan (Anexo VI). Vacío = el plan no lo dice; 0 = lo dice.
  { header: 'ALB_AREA_CERR_M2', key: 'albAreaCerrM2', width: 16, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_AREA_ABIER_M2', key: 'albAreaAbierM2', width: 17, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_INODOROS', key: 'albInodoros', width: 13, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_LAVABOS', key: 'albLavabos', width: 12, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_DUCHAS', key: 'albDuchas', width: 11, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_HABITACIONES', key: 'albHabitaciones', width: 16, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_CAMAS', key: 'albCamas', width: 10, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_MANTAS', key: 'albMantas', width: 11, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_ESTANTERIAS', key: 'albEstanterias', width: 15, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_ELECTRICIDAD', key: 'albElectricidad', width: 16, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_AGUA', key: 'albAgua', width: 9, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_GENERADOR', key: 'albGenerador', width: 14, style: { alignment: { horizontal: 'center' } } },
  { header: 'ALB_EQUIPAMIENTO', key: 'albEquipamiento', width: 30 },
  { header: 'ALB_OTROS', key: 'albOtros', width: 30 },
  { header: 'ALB_NOTAS', key: 'albNotas', width: 40 },
  // ALBRTA-b2c (DOP-1 a): plazas del Registro de Turismo de Andalucía publicado (OpenRTA),
  // aparte del aforo declarado y de la capacidad DGPC; nunca se suman entre sí.
  { header: 'RTA_PLAZAS', key: 'rtaPlazas', width: 12, style: { alignment: { horizontal: 'center' } } },
  { header: 'RTA_INSCR', key: 'rtaInscr', width: 50 },
  { header: 'RTA_AVISOS', key: 'rtaAvisos', width: 50 },
  { header: 'REF_CATASTRAL', key: 'refCatastral', width: 22 },
  { header: 'CAT_SUPERFICIE', key: 'catastroSuperficie', width: 15, style: { numFmt: '0', alignment: { horizontal: 'center' } } },
  { header: 'CAT_PLANTAS', key: 'catastroNplantas', width: 12, style: { alignment: { horizontal: 'center' } } },
  { header: 'CAT_USO', key: 'catastroUso', width: 22 },
  { header: 'CAT_AFORO_METODO', key: 'catastroAforoMetodo', width: 16 },
  { header: 'CAT_ACCESIBILIDAD', key: 'catastroAccesibilidad', width: 18 },
  { header: 'CAT_ANO_CONSTR',      key: 'catastroAnoConstr',     width: 14,
    style: { numFmt: '0', alignment: { horizontal: 'center' } } },
  { header: 'CAT_AFORO_ESTIMADO',  key: 'catastroAforoEstimado', width: 18,
    style: { numFmt: '0', alignment: { horizontal: 'center' } } },
  // CAT-R2.01: validación espacial buildings
  { header: 'CAT_EN_EDIFICIO', key: 'catastroEnEdificio', width: 16, style: { alignment: { horizontal: 'center' } } },
  { header: 'CAT_EDIFICIO_RC', key: 'catastroEdificioRc', width: 20 },
  // UMBRAL-01: banda de calidad posicional (INVEST-01, 2026-03-27)
  { header: 'BANDA_POSICIONAL', key: 'bandaPosicional', width: 18, style: { alignment: { horizontal: 'center' } } },
  // EXP-D01-D: Notas legibles para técnico municipal (warnings normalización)
  { header: 'NOTAS_VALIDACION', key: 'notasValidacion', width: 60 },
  { header: 'ALERTAS', key: 'alertas', width: 50 },
  { header: 'URL_GMAPS', key: 'urlGMaps', width: 60 },
  { header: 'URL_OSM', key: 'urlOSM', width: 60 },
  { header: 'URL_DERA', key: 'urlDERA', width: 60 },
];

/**
 * Colores de score para formato condicional
 */
export const SCORE_COLORS = {
  alta: { bg: 'FFC8E6C9', text: '2E7D32' },      // Verde
  media: { bg: 'FFFFF9C4', text: 'F57F17' },     // Amarillo
  baja: { bg: 'FFFFE0B2', text: 'E65100' },      // Naranja
  critica: { bg: 'FFFFCDD2', text: 'C62828' },   // Rojo
};

/**
 * Columnas específicas para pestaña Sin_Geolocalizar (F5.06)
 * Incluye MOTIVO explicativo para técnicos de Protección Civil
 */
export const FAILED_SHEET_COLUMNS: ExcelColumn[] = [
  { header: 'ID', key: 'id', width: 8 },
  { header: 'NOMBRE', key: 'nombre', width: 35 },
  { header: 'TIPOLOGIA', key: 'tipologia', width: 15 },
  { header: 'TIPO_GEO', key: 'tipoGeo', width: 20 },
  { header: 'MOTIVO', key: 'motivo', width: 55 },
  { header: 'DIRECCION', key: 'direccion', width: 40 },
  { header: 'MUNICIPIO', key: 'municipio', width: 20 },
  { header: 'SCORE', key: 'score', width: 10, style: { alignment: { horizontal: 'center' } } },
  { header: 'X_INICIAL', key: 'xInicial', width: 15, style: { numFmt: '0.00' } },
  { header: 'Y_INICIAL', key: 'yInicial', width: 15, style: { numFmt: '0.00' } },
  { header: 'ALERTAS', key: 'alertas', width: 50 },
];

/**
 * Configuración de fuentes de datos
 */
export const SOURCE_CONFIG = {
  DOC: { label: 'Documento Original', color: { dark: '#A3E635', light: '#65A30D' } },
  IGN: { label: 'IGN CartoCiudad', color: { dark: '#38BDF8', light: '#0369A1' } },
  DERA: { label: 'DERA Junta', color: { dark: '#C084FC', light: '#9333EA' } },
  CDAU: { label: 'CDAU Direcciones', color: { dark: '#2DD4BF', light: '#0D9488' } },
  CATASTRO: { label: 'Catastro', color: { dark: '#FB923C', light: '#EA580C' } },
  CARTOCIUDAD: { label: 'CartoCiudad', color: { dark: '#818CF8', light: '#4F46E5' } },
};

/**
 * Límites geográficos de Andalucía (UTM30 ETRS89)
 */
export const ANDALUCIA_BOUNDS = {
  xMin: 100000,
  xMax: 620000,
  yMin: 3980000,
  yMax: 4290000,
};

/**
 * Sistemas de referencia soportados
 */
export const CRS_CONFIG = {
  'EPSG:25830': { name: 'UTM30 ETRS89', isDefault: true },
  'EPSG:25829': { name: 'UTM29 ETRS89', isDefault: false },
  'EPSG:4326': { name: 'WGS84', isDefault: false },
  'EPSG:4258': { name: 'ETRS89', isDefault: false },
};

/**
 * Mensajes de UI
 */
export const UI_MESSAGES = {
  noData: 'No hay datos para exportar',
  noFormats: 'Selecciona al menos un formato',
  exporting: 'Exportando...',
  exportSuccess: 'Exportación completada',
  exportError: 'Error en la exportación',
  noGeoForFormat: 'Este formato requiere registros geolocalizados',
};

/**
 * Configuración del mapa
 */
export const MAP_CONFIG = {
  defaultCenter: { lat: 37.3891, lng: -5.9845 }, // Centro de Andalucía
  defaultZoom: 8,
  minZoom: 6,
  maxZoom: 18,
  tileProviders: {
    osm: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    pnoa: 'https://www.ign.es/wmts/pnoa-ma?service=WMTS&request=GetTile&version=1.0.0&layer=OI.OrthoimageCoverage&style=default&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}&format=image/jpeg',
    catastro: 'https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx',
  },
};
